import PropTypes from 'prop-types';
import React from 'react';
import { graphql } from 'gatsby';
import { css } from '@emotion/core';
import _ from 'lodash/fp';
import {
  breakpoints,
  colors,
  fontWeights,
  unitConverter as uc,
} from '../../../styles/base';
import Layout from '../../organisms/Layout/Layout';
import { Heading3 } from '../../atoms/Headings/Headings';
import Link from '../../atoms/Link/Link';
import Button from '../../atoms/Buttons/Button/Button';
import PressReleaseTile from '../../molecules/PressReleaseTile/PressReleaseTile';
import NewsCoverageTile from '../../molecules/NewsCoverageTile/NewsCoverageTile';
import { renderPageSections } from '../renderPageSections';
import PressContact from '../../molecules/PressContact/PressContact';

const rowCss = css`
  display: flex;
  flex-flow: row wrap;
  justify-content: space-between;
  width: 100%;
  max-width: ${uc('1180px')};
  padding: ${uc('0 10px')};

  @media (${breakpoints.smallDesktopMin}) {
    margin: auto;
  }

  @media (${breakpoints.mobile}) {
    padding-right: ${uc('40px')};
    padding-left: ${uc('40px')};
  }
`;

const bannerCss = css`
  img {
    margin-left: ${uc('-30px')};
    border-radius: ${uc('20px')};
    transform: scale(0.8);

    @media (${breakpoints.tablet}) {
      margin-top: ${uc('-60px')};
      margin-left: ${uc('0px')};
    }

    @media (${breakpoints.mobile}) {
      margin-top: ${uc('-40px')};
      transform: none;
    }
  }

  @media (${breakpoints.smallDesktopMin}) {
    & > div {
      margin: auto;
    }
  }

  @media (${breakpoints.tablet}) {
    padding-top: ${uc('110px')};
  }

  @media (${breakpoints.mobile}) {
    padding-top: ${uc('90px')};
  }
`;

const colLeftCss = css`
  width: ${uc('932px')};

  @media (${breakpoints.smallDesktopMax}) {
    width: 100%;
  }
`;

const colRightCss = css`
  width: ${uc('228px')};
  margin: ${uc('65px 0')};

  @media (${breakpoints.smallDesktopMax}) {
    display: flex;
    justify-content: space-around;
    width: 100%;
  }
`;

const centerCss = css`
  @media (${breakpoints.smallDesktopMax}) {
    text-align: center;
  }
`;

const articleCss = css`
  display: flex;
  flex-flow: row wrap;
  justify-content: flex-start;
  width: 100%;
  margin-bottom: ${uc('16px')};

  @media (${breakpoints.smallDesktopMax}) {
    justify-content: center;
  }
`;

const mainContainerCss = css`
  padding-left: ${uc('0px')};
  background-color: ${colors.white};

  @media (${breakpoints.smallDesktopMin}) {
    padding-top: ${uc('80px')};
  }

  @media (${breakpoints.mobile}) {
    padding-top: ${uc('40px')};
  }
`;

const sectionCss = css`
  margin-bottom: ${uc('60px')};
`;

const linksCss = css`
  display: inline-grid;
  line-height: ${uc('25px')};

  ${Heading3} {
    margin-bottom: ${uc('5px')};
  }
`;

const sectionAndLinksCss = css`
  ${sectionCss}
  ${linksCss}
`;

const linkCss = css`
  color: ${colors.accent};
  font-weight: ${fontWeights.semiBold};
  font-size: ${uc('15px')};
  text-decoration: none;
`;

const NewsRoomPage = ({ data, location: { href } }) => {
  if (!data) {
    return null;
  }

  const newsRoomPageProps = {
    id: data.newsRoomPage._id,
    metadata: { ...data.newsRoomPage._rawRoute, href },
    banner: {
      ...data.newsRoomPage._rawBanner,
      css: bannerCss,
    },
    pressReleasesTitle: data.newsRoomPage.pressReleasesTitle,
    allPressReleasesTitle: data.newsRoomPage.allPressReleasesTitle,
    newsCoverageTitle: data.newsRoomPage.newsCoverageTitle,
    allNewsCoveragesTitle: data.newsRoomPage.allNewsCoveragesTitle,
    analystReportsTitle: data.newsRoomPage.analystReportsTitle,
    awardsRecognitionTitle: data.newsRoomPage.awardsRecognitionTitle,
    linksTitle: data.newsRoomPage.linksTitle,
    links: data.newsRoomPage.links,
    pressContact: data.newsRoomPage.pressContact,
  };

  const analystReports = _.getOr([], 'newsRoomPage.analystReports', data);
  const awardsRecognition = _.getOr([], 'newsRoomPage.awardsRecognition', data);

  return (
    <Layout
      menuColor="white"
      sanityId={newsRoomPageProps.id}
      {...newsRoomPageProps.metadata}
    >
      {renderPageSections([newsRoomPageProps.banner])}
      <div css={mainContainerCss}>
        <div css={rowCss}>
          <div css={colLeftCss}>
            <section css={sectionCss}>
              <div css={centerCss}>
                <Heading3>{newsRoomPageProps.pressReleasesTitle}</Heading3>
              </div>
              <div css={articleCss}>
                {_.map(({ node: pressRelease }) => {
                  if (pressRelease) {
                    const {
                      _id,
                      postDate,
                      route: {
                        slug: { current },
                      },
                      title,
                    } = pressRelease;
                    return (
                      <PressReleaseTile
                        key={_id}
                        title={title}
                        postDate={postDate}
                        slug={current}
                      />
                    );
                  }
                  return null;
                }, _.getOr([], 'recentPressReleases.edges', data))}
              </div>
              <div css={centerCss}>
                <Button
                  link="/search?facetFilters[_type]=pressReleasePage"
                  color="gray"
                >
                  {newsRoomPageProps.allPressReleasesTitle}
                </Button>
              </div>
            </section>
            <section css={sectionCss}>
              <div css={centerCss}>
                <Heading3>{newsRoomPageProps.newsCoverageTitle}</Heading3>
              </div>
              <div css={articleCss}>
                {_.map(
                  ({ _id, link, logo, title }) => (
                    <NewsCoverageTile
                      key={_id}
                      title={title}
                      subtitle="Featured"
                      link={link}
                      image={logo}
                    />
                  ),
                  _.getOr([], 'newsRoomPage.featuredNewsCoverages', data)
                )}
              </div>
              <div css={centerCss}>
                <Button link="/company/news/in-the-news" color="gray">
                  {newsRoomPageProps.allNewsCoveragesTitle}
                </Button>
              </div>
            </section>
            {_.some(_analystReport => {
              const link = _.getOr(
                '',
                'pressReleasePage.route.slug.current',
                _analystReport
              );
              return Boolean(link);
            }, analystReports) && (
              <section css={sectionCss}>
                <div css={centerCss}>
                  <Heading3>{newsRoomPageProps.analystReportsTitle}</Heading3>
                </div>
                <div css={articleCss}>
                  {_.map(analystReport => {
                    if (analystReport) {
                      const {
                        _key,
                        companyName,
                        tileImage,
                        title,
                      } = analystReport;
                      const link = _.getOr(
                        '',
                        'pressReleasePage.route.slug.current',
                        analystReport
                      );
                      return (
                        <NewsCoverageTile
                          key={_key}
                          title={title}
                          subtitle={companyName}
                          link={link}
                          image={tileImage}
                        />
                      );
                    }
                    return null;
                  }, analystReports)}
                </div>
              </section>
            )}
            {_.some(
              _ar => Boolean(_.getOr(null, 'newsCoverage.link', _ar)),
              awardsRecognition
            ) && (
              <section css={sectionCss}>
                <div css={centerCss}>
                  <Heading3>
                    {newsRoomPageProps.awardsRecognitionTitle}
                  </Heading3>
                </div>
                <div css={articleCss}>
                  {_.map(_ar => {
                    if (_ar) {
                      const {
                        _key,
                        companyName,
                        newsCoverage: { link, logo, title },
                      } = _ar;
                      return (
                        <NewsCoverageTile
                          key={_key}
                          title={title}
                          subtitle={companyName}
                          link={link}
                          image={logo}
                        />
                      );
                    }
                    return null;
                  }, awardsRecognition)}
                </div>
              </section>
            )}
          </div>
          <div css={colRightCss}>
            {newsRoomPageProps.pressContact && (
              <section css={sectionCss}>
                <PressContact pressContact={newsRoomPageProps.pressContact} />
              </section>
            )}
            {!_.isEmpty(newsRoomPageProps.links) && (
              <section css={sectionAndLinksCss}>
                <Heading3>{newsRoomPageProps.linksTitle}</Heading3>
                {newsRoomPageProps.links.map(link => (
                  <Link key={link.text} to={link} css={linkCss}>
                    {link.text}
                  </Link>
                ))}
              </section>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

NewsRoomPage.propTypes = {
  data: PropTypes.shape({ newsRoomPage: {} }),
  location: PropTypes.shape({ href: PropTypes.string }),
};

NewsRoomPage.defaultProps = {
  data: {},
  location: { href: '' },
};

export default NewsRoomPage;

export const query = graphql`
  query($slug: String) {
    newsRoomPage: sanityNewsRoomPage(
      route: { slug: { current: { eq: $slug } } }
    ) {
      _id
      _rawRoute(resolveReferences: { maxDepth: 100 })
      _rawBanner(resolveReferences: { maxDepth: 10 })
      pressReleasesTitle
      allPressReleasesTitle
      newsCoverageTitle
      allNewsCoveragesTitle
      featuredNewsCoverages {
        _id
        postDate(formatString: "MMMM DD, YYYY")
        title
        logo {
          asset {
            fluid {
              src
            }
            _id
            _rawMetadata
          }
          _type
        }
        link {
          link {
            ... on SanityExternalLink {
              externalLink
            }
          }
          text
        }
      }
      analystReportsTitle
      analystReports {
        _key
        title
        companyName
        tileImage {
          asset {
            fluid {
              src
            }
            _id
            _rawMetadata
          }
          _type
        }
        pressReleasePage {
          route {
            slug {
              current
            }
          }
        }
      }
      awardsRecognitionTitle
      awardsRecognition {
        _key
        companyName
        newsCoverage {
          link {
            link {
              ... on SanityExternalLink {
                externalLink
              }
            }
          }
          logo {
            asset {
              fluid {
                src
              }
              _id
            }
            _type
          }
          title
        }
      }
      linksTitle
      links {
        text
        link {
          ... on SanityExternalLink {
            _type
            externalLink
          }
          ... on SanityRoute {
            slug {
              current
            }
          }
        }
      }
      pressContact {
        email
        phone
        title
        name
      }
    }
    recentPressReleases: allSanityPressReleasePage(
      limit: 3
      sort: { fields: postDate, order: DESC }
      filter: {
        _id: { regex: "/^((?!drafts).)*$/" }
        route: { enabled: { eq: true } }
        scheduledPublishTime: { utc: { eq: null } }
      }
    ) {
      edges {
        node {
          title
          postDate(formatString: "MMMM DD, YYYY")
          _id
          route {
            slug {
              current
            }
          }
        }
      }
    }
  }
`;
